/**
 * Date utility functions to handle timezone issues consistently.
 *
 * THE PROBLEM:
 * When you do `new Date("2025-12-29")`, JavaScript interprets it as UTC midnight.
 * Then `toLocaleDateString()` converts to local timezone, shifting the date back
 * by one day for users west of UTC (e.g., EST, PST).
 *
 * THE SOLUTION:
 * Parse date-only strings (YYYY-MM-DD) by extracting components and creating
 * a local date, avoiding UTC interpretation entirely.
 *
 * USAGE:
 * import { formatDate, formatDateShort, parseLocalDate } from '../utils/dateUtils';
 *
 * // For displaying dates from API (YYYY-MM-DD format)
 * formatDate(transaction.transaction_date)  // "Dec 29, 2025"
 * formatDateShort(statement.closing_date)   // "Dec 29"
 *
 * // For getting a Date object that respects local timezone
 * const date = parseLocalDate("2025-12-29");
 */

/**
 * Parse a date string (YYYY-MM-DD) into a local Date object.
 * This avoids the UTC interpretation issue.
 *
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date|null} - Local Date object or null if invalid
 */
export function parseLocalDate(dateString) {
  if (!dateString) return null;

  // Handle ISO datetime strings (with time component)
  if (dateString.includes('T')) {
    // For full ISO strings, just use the date part
    dateString = dateString.split('T')[0];
  }

  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(p => parseInt(p, 10));
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  // Month is 0-indexed in JavaScript Date
  return new Date(year, month - 1, day);
}

/**
 * Format a date string for display (e.g., "Dec 29, 2025")
 *
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} - Formatted date string
 */
export function formatDate(dateString, options = {}) {
  if (!dateString) return 'N/A';

  const date = parseLocalDate(dateString);
  if (!date) return 'Invalid Date';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  return date.toLocaleDateString('en-US', defaultOptions);
}

/**
 * Format a date string in short format (e.g., "Dec 29")
 *
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export function formatDateShort(dateString) {
  return formatDate(dateString, { year: undefined });
}

/**
 * Format a date string with full month name (e.g., "December 29, 2025")
 *
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date string
 */
export function formatDateLong(dateString) {
  return formatDate(dateString, { month: 'long' });
}

/**
 * Format a date for input fields (YYYY-MM-DD)
 *
 * @param {Date|string} date - Date object or string
 * @returns {string} - Date in YYYY-MM-DD format
 */
export function formatDateForInput(date) {
  if (!date) return '';

  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format a datetime string for display (e.g., "Dec 29, 2025 at 3:45 PM")
 * Use this for timestamps that include time component.
 *
 * @param {string} dateTimeString - ISO datetime string
 * @returns {string} - Formatted datetime string
 */
export function formatDateTime(dateTimeString) {
  if (!dateTimeString) return 'N/A';

  // For full datetime strings, we CAN use new Date() directly
  // because the time component makes timezone handling correct
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return 'Invalid Date';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * Check if a date string is valid
 *
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid
 */
export function isValidDate(dateString) {
  const date = parseLocalDate(dateString);
  return date !== null && !isNaN(date.getTime());
}

/**
 * Get today's date in YYYY-MM-DD format
 *
 * @returns {string} - Today's date
 */
export function getTodayString() {
  return formatDateForInput(new Date());
}

/**
 * Calculate days between two dates
 *
 * @param {string} dateString1 - First date (YYYY-MM-DD)
 * @param {string} dateString2 - Second date (YYYY-MM-DD)
 * @returns {number} - Number of days (positive if date2 > date1)
 */
export function daysBetween(dateString1, dateString2) {
  const date1 = parseLocalDate(dateString1);
  const date2 = parseLocalDate(dateString2);

  if (!date1 || !date2) return 0;

  const diffTime = date2.getTime() - date1.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}
